import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Output } from "valibot";

import { useGlobalsContext } from "shared/hooks";
import { ActionSchema, ActionsSchema } from "shared/schemas";

import { fetchCommand } from "./functions";

function useScheduledActions(
  actions: Output<typeof ActionsSchema>,
  botId: string,
  token: string,
  onSendRef: React.MutableRefObject<
    ((msg: string, chatUserId?: string) => Promise<void>) | undefined
  >,
) {
  const {
    getParentUrl,
    parentWindowScroll,
    setWidgetExpanded,
    setCurrentUserFormData,
    currentUserFormData,
  } = useGlobalsContext();

  const [scrollActions, setScrollActions] = useState<
    Output<typeof ActionsSchema>
  >([]);

  const scheduleAction = useCallback(
    (action: Output<typeof ActionSchema>) => {
      for (const command of action.commands) {
        fetchCommand(
          command,
          setWidgetExpanded,
          token,
          onSendRef,
          setCurrentUserFormData,
          currentUserFormData,
        );
      }
    },
    [
      setWidgetExpanded,
      token,
      onSendRef,
      setCurrentUserFormData,
      currentUserFormData,
    ],
  );

  useEffect(() => {
    if (!scrollActions.length) {
      return;
    }

    const completedActions: Output<typeof ActionsSchema> = [];

    for (const scrollAction of scrollActions) {
      if (parentWindowScroll >= Number(scrollAction.scrollPercentage)) {
        for (const command of scrollAction.commands) {
          fetchCommand(
            command,
            setWidgetExpanded,
            token,
            onSendRef,
            setCurrentUserFormData,
            currentUserFormData,
          );
        }
        completedActions.push(scrollAction);
      }
    }

    if (completedActions.length) {
      setScrollActions(prevScrollActions =>
        prevScrollActions.filter(
          prevScrollAction => !completedActions.includes(prevScrollAction),
        ),
      );
    }
  }, [
    setWidgetExpanded,
    onSendRef,
    scrollActions,
    parentWindowScroll,
    token,
    setCurrentUserFormData,
    botId,
    currentUserFormData,
  ]);

  useLayoutEffect(() => {
    if (!actions.length || !botId) {
      return;
    }
    const timeouts: NodeJS.Timeout[] = [];
    const scheduleActions = async () => {
      const parentUrl = await new Promise<URL>(resolve =>
        getParentUrl(resolvedParentUrl => resolve(resolvedParentUrl)),
      );

      const newScrollActions = [];

      for (const action of actions) {
        const urlFits =
          !action.urls ||
          action.urls.some(url => {
            const urlRegExp = new RegExp(url);
            return urlRegExp.test(parentUrl.href);
          });
        if (!urlFits) {
          continue;
        }
        if (action.scrollPercentage) {
          newScrollActions.push(action);
        }
        if (!action.scrollPercentage || action.timeout) {
          const timeout = setTimeout(
            () => scheduleAction(action),
            action.timeout || 0,
          );
          timeouts.push(timeout);
        }
      }

      setScrollActions(newScrollActions);
    };

    scheduleActions();

    return () => {
      for (const timeout of timeouts) {
        clearTimeout(timeout);
      }
    };
  }, [getParentUrl, scheduleAction, actions, botId]);
}

export default useScheduledActions;
